/**
 * Admin Settings Hub — /admin/settings
 *
 * Feb 2026 — Central index of every admin settings & pricing surface
 * so admins don't need to memorise deep URLs. Each card is a Link to
 * the concrete page. Kept intentionally lean — one Link per settings
 * page, grouped into Pricing vs Operations.
 */
import { Link } from "react-router-dom";
import {
  Percent,
  Tag,
  Users,
  Flag,
  Package,
  CalendarClock,
  FileSpreadsheet,
  ArrowRight,
  BellRing,
  DollarSign,
  Mailbox,
  ClipboardCheck,
  Droplets,
} from "lucide-react";
import { PortalShell } from "@/components/PortalShell";

const PRICING = [
  {
    to: "/admin/settings/pricing-and-fulfilment",
    icon: DollarSign,
    title: "Pricing & Fulfilment",
    blurb: "All-inclusive package prices, mail-out kit settings, local-lab collection ($20 saving), partner-supplied kit credits, fulfilment rules and audit history.",
    testId: "settings-hub-pricing-and-fulfilment",
  },
  {
    to: "/admin/settings/online-discounts",
    icon: Percent,
    title: "Online Discount Control",
    blurb: "Toggle the public online-prepaid discount site-wide, adjust the percentage and pill wording.",
    testId: "settings-hub-online-discounts",
  },
  {
    to: "/admin/pricing/test-catalog",
    icon: Tag,
    title: "Test Catalog Pricing",
    blurb: "Override retail / member / online-prepaid prices for each test in the catalog.",
    testId: "settings-hub-test-catalog",
  },
  {
    to: "/admin/pricing/partner-types",
    icon: Users,
    title: "Partner Pricing",
    blurb: "Per partner-type price grids (property managers, trade partners, PWS franchisees).",
    testId: "settings-hub-partner-pricing",
  },
];

const OPERATIONS = [
  {
    to: "/admin/tank-water-health-checks",
    icon: Droplets,
    title: "Tank Water Health Checks",
    blurb: "Campaign Health Checks: technician assignment, office SMS alerts (Stephen / Megan), AWARE sample collection state, lab test linkage, reschedule/cancel and test-record cleanup.",
    testId: "settings-hub-tank-water-health-checks",
  },
  {
    to: "/admin/property-campaigns",
    icon: Mailbox,
    title: "Property Campaigns",
    blurb: "Direct-mail campaigns: CSV import, QR letters, appointment slots, water health checks, funnel tracking.",
    testId: "settings-hub-property-campaigns",
  },
  {
    to: "/field/today",
    icon: ClipboardCheck,
    title: "Field Inspection App (Practice)",
    blurb: "Open the technician Water Health inspection app as admin — you see all appointments, including the permanent 'Practice Street' job, to rehearse and check the form.",
    testId: "settings-hub-field-practice",
  },
  {
    to: "/admin/settings/features",
    icon: Flag,
    title: "Feature Flags",
    blurb: "Turn beta features on/off (Starshipit, review flows, etc.).",
    testId: "settings-hub-features",
  },
  {
    to: "/admin/settings/posted-kit",
    icon: Package,
    title: "Posted-Kit Settings",
    blurb: "Postage fee, freight-included threshold and kit inventory rules for mailed samples.",
    testId: "settings-hub-posted-kit",
  },
  {
    to: "/admin/settings/booking-availability",
    icon: CalendarClock,
    title: "Booking Availability",
    blurb: "Site-wide booking pause / holiday banner shown to customers on the booking page.",
    testId: "settings-hub-booking-availability",
  },
  {
    to: "/admin/settings/new-booking-notifications",
    icon: BellRing,
    title: "New Booking Notifications",
    blurb: "Choose which internal team members are emailed when a new booking is created, and edit the notification template.",
    testId: "settings-hub-new-booking-notifications",
  },
  {
    to: "/admin/settings/xero-upload",
    icon: FileSpreadsheet,
    title: "Xero Upload",
    blurb: "Configure the Xero contact CSV export & Xero API credentials.",
    testId: "settings-hub-xero-upload",
  },
  {
    to: "/admin/settings/bulk-kit",
    icon: Package,
    title: "Bulk Kit Settings",
    blurb: "Pricing per kit + per-box parcel dimensions passed to Starshipit on partner bulk orders.",
    testId: "settings-hub-bulk-kit",
  },
];

const INTEGRATIONS = [
  {
    to: "/admin/settings/servicem8",
    icon: CalendarClock,
    title: "ServiceM8 Scheduling",
    blurb: "Technician conflict checks for Property Campaign bookings — staff mapping, scheduling rules, availability preview.",
    testId: "settings-hub-servicem8",
  },
  {
    to: "/admin/settings/bing-mail",
    icon: Mailbox,
    title: "Bing Mail (EasyPost)",
    blurb: "Active provider for Property Campaign letter printing & postage. Test/Live mode, hold-for-release, address window layout.",
    testId: "settings-hub-bing-mail",
  },
  {
    to: "/admin/settings/postgrid",
    icon: Mailbox,
    title: "PostGrid Print & Mail (historical)",
    blurb: "Former mail provider — kept for historical mailout records. New mailouts use Bing Mail.",
    testId: "settings-hub-postgrid",
  },
];

function SettingsCard({ to, icon: Icon, title, blurb, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="group relative rounded-2xl bg-white border border-slate-200 p-5 hover:border-cyan-500 hover:shadow-md transition-all flex flex-col"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-cyan-50 text-cyan-700">
          <Icon className="w-4 h-4" />
        </div>
        <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-cyan-600 group-hover:translate-x-0.5 transition-all" />
      </div>
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      <p className="mt-1 text-xs text-slate-500 leading-relaxed">{blurb}</p>
      <div className="mt-3 text-[10px] font-mono text-slate-400 tracking-tight truncate">{to}</div>
    </Link>
  );
}

function Section({ title, items }) {
  return (
    <div className="mb-8">
      <h2 className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">{title}</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((it) => (
          <SettingsCard key={it.to} {...it} />
        ))}
      </div>
    </div>
  );
}

export default function AdminSettingsHub() {
  return (
    <PortalShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="mb-8">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700">Admin</div>
          <h1 data-testid="settings-hub-heading" className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 mt-2">Settings</h1>
          <p className="text-sm text-slate-500 mt-2">
            All admin configuration pages in one place. Pricing changes take effect immediately across the public site.
          </p>
        </div>

        <Section title="Pricing" items={PRICING} />
        <Section title="Operations" items={OPERATIONS} />
        <Section title="Integrations" items={INTEGRATIONS} />
      </div>
    </PortalShell>
  );
}
